/**
 * Halleck Vineyard variant — UI overrides + extras for tenant `halleckvineyard`.
 *
 * REGISTERED EXTRAS:
 *   - kind: 'live-person-form' — base LivePersonForm (RedChirp SMS opt-in)
 *   - kind: 'vineyard-tours-card' — /vineyard-tours/ promo (image + Learn More)
 *   - Component overrides: EventCard, EventDetailsCard, MembershipCard
 *
 * AUTO-OPEN (old main.js autoOpenChat):
 *   - Homepage / local test.html: first visit only (localStorage), refresh → no auto-open
 *   - /vineyard-tours/: ALWAYS auto-open on every visit/refresh (old condition3, no
 *     cookie) + vineyard card (no guided chips). Leave that page → normal chat
 *     (welcome + chips, no vineyard card).
 *
 * REDCHIRP: features.redchirp is admin-driven (already true for Halleck).
 *
 * Reference: old main.js autoOpenChat / addVineyardCard / showContactForm;
 *   halleck script.js showDefaultOptions / starterTextLivePerson.
 */

import {
  on,
  emit,
  addMessage,
  removeMessage,
  getMessages,
  getState,
  setState,
  updateChatState,
  setMessages,
} from "../../core/state.js";
import { getTenantId, getTheme } from "../../core/configAccessors.js";
import { registerMessageKind } from "../../components/base/messages/index.js";
import { registry } from "../../core/registry.js";
import LivePersonForm from "../../components/base/messages/LivePersonForm.js";
import HalleckWineCard from "./components/WineCard.js";
import HalleckEventCard from "./components/EventCard.js";
import HalleckEventDetailsCard from "./components/EventDetailsCard.js";
import HalleckMembershipCard from "./components/MembershipCard.js";
import VineyardToursCard from "./components/VineyardToursCard.js";

const GUIDED_FLOW = [
  { label: "Experience our wines",     value: "Show me all tasting experiences",   icon: "bolt" },
  { label: "Shop our wines",           value: "Show me all available wines",       icon: "bolt" },
  { label: "Explore our memberships",  value: "Show me all wine club memberships", icon: "bolt" },
  { label: "Events and travel",        value: "Show me all the upcoming events",   icon: "bolt", visible_when: { is_event: true } },
  { label: "Reviews",                  value: "Show me all the reviews",           icon: "bolt" },
  { label: "Text a Live Person",       value: "__text_live_person__",              icon: "bolt" },
];

const VT_INTRO =
  "Looking to experience our wines? Give us a call or text us to learn about our options. Jennifer - 707-738-8383 Alison - 707-486-5061\n\nEnter your information below and we will text you between the hours of 9AM to 5PM PST.";

const VT_DEFAULT_INTRO =
  "Enter your information and we will text you between the hours of 9AM to 5PM PST.";

const LOCAL_DEV_HOSTS = new Set(["localhost", "127.0.0.1"]);

export function getGuidedFlow() {
  const { isQR, openagent } = getState().session || {};
  const showTasting = !isQR || openagent;
  return GUIDED_FLOW.filter(
    (item) => showTasting || item.value !== "Show me all tasting experiences"
  );
}

function postDefaultOptions() {
  addMessage({ role: "bot", kind: "text", data: { text: "Is there anything else I can help you with?" } });
  addMessage({ role: "bot", kind: "prompts", data: { items: getGuidedFlow() } });
}

function storageTenantKey() {
  return getTenantId() || "halleckvineyard";
}

function lsGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage blocked */
  }
}

function isSkippedCommercePath() {
  const path = window.location.pathname || "";
  return path.startsWith("/cart") || path.startsWith("/checkouts") || path === "/cart";
}

function isVineyardToursPage() {
  if (isSkippedCommercePath()) return false;
  const path = window.location.pathname || "";
  if (path.includes("/vineyard-tours")) return true;

  // Local test.html helpers:
  //   ?tenant=halleckvineyard&path=/vineyard-tours/
  //   ?tenant=halleckvineyard&vineyard_tours=1
  //   ?tenant=halleckvineyard/vineyard-tours/
  try {
    const params = new URLSearchParams(window.location.search || "");
    const sim = params.get("path") || params.get("page") || "";
    if (sim.includes("vineyard-tours")) return true;
    const flag = params.get("vineyard_tours");
    if (flag === "1" || flag === "true") return true;
    const tenant = params.get("tenant") || "";
    if (tenant.includes("vineyard-tours")) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** Homepage (or local test.html) — old autoOpenChat condition2. */
function isHalleckHomePage() {
  if (isSkippedCommercePath() || isVineyardToursPage()) return false;
  const path = window.location.pathname || "";
  const host = window.location.hostname || "";

  if (
    (host === "halleckvineyard.com" || host === "www.halleckvineyard.com") &&
    (path === "/" || path === "")
  ) {
    return true;
  }
  if (host === "shop.halleckvineyard.com" && path.includes("/collections/all")) {
    return true;
  }
  // Local widget test page without vineyard-tours simulation
  if (LOCAL_DEV_HOSTS.has(host)) {
    if (path === "/" || path === "" || /\/test\.html$/i.test(path)) return true;
  }
  return false;
}

// ── Homepage one-time flag (old _vi cookie). Vineyard-tours has NO flag —
// old condition3 always auto-opens on that path.
function homeSeenKey() {
  return `ws_home_auto_open_${storageTenantKey()}`;
}
function hasSeenHomeAutoOpen() {
  return lsGet(homeSeenKey()) === "1";
}
function markHomeAutoOpenSeen() {
  lsSet(homeSeenKey(), "1");
}

function openChatSoon() {
  setTimeout(() => {
    if (getState().chat?.isOpen) return;
    updateChatState({ isOpen: true });
    emit("ws-toggle", { isOpen: true });
  }, 2000);
}

/** Old condition3 — every /vineyard-tours/ load auto-opens (including refresh). */
function maybeAutoOpenVineyardTours() {
  if (!isVineyardToursPage()) return;
  openChatSoon();
}

function maybeAutoOpenHome() {
  if (!isHalleckHomePage() || hasSeenHomeAutoOpen()) return;
  markHomeAutoOpenSeen();
  openChatSoon();
}

function stripVineyardToursCards() {
  const msgs = getMessages();
  const kept = msgs.filter((m) => m.kind !== "vineyard-tours-card");
  if (kept.length !== msgs.length) setMessages(kept);
}

/**
 * Vineyard first-visit removes guided chips from the transcript and persists
 * that to localStorage. On a normal page / return visit, restore chips after
 * the welcome so the SS "starting prompts" show again.
 * Must run before ChatPanel/MessageList renders (register is before init).
 */
function ensureGuidedPromptsInState() {
  const msgs = getMessages();
  if (msgs.length === 0) return; // MessageList._seed will add welcome + prompts
  if (msgs.some((m) => m.kind === "prompts")) return;

  const items = getGuidedFlow();
  if (!items.length) return;

  const promptMsg = {
    id: `m_prompts_restore_${Date.now()}`,
    role: "bot",
    kind: "prompts",
    isFirstResponse: false,
    data: { items },
    meta: {},
    timestamp: Date.now(),
  };

  const welcomeIdx = msgs.findIndex((m) => m.meta?.isWelcome);
  if (welcomeIdx >= 0) {
    const next = msgs.slice();
    next.splice(welcomeIdx + 1, 0, promptMsg);
    setMessages(next);
  } else {
    setMessages([...msgs, promptMsg]);
  }
}

function showLivePersonForm({ fromVineyardTours = false } = {}) {
  addMessage({
    role: "bot",
    kind: "text",
    data: { text: fromVineyardTours ? VT_INTRO : VT_DEFAULT_INTRO },
  });
  const brand = (getState().tenant || {}).display_name || "Halleck Vineyard";
  addMessage({ role: "bot", kind: "live-person-form", data: { brand } });
}

function scrollVineyardCardIntoView() {
  // Defer so this runs AFTER MessageList's _keepTopForWelcome on the same
  // ws-welcome-seeded turn — otherwise keepTop's _scrollGen cancels the pin
  // and the promo poster stays focused (ss1). We want the vineyard card +
  // LEARN MORE in view (ss2).
  setTimeout(() => {
    emit("ws-pin-scroll-to", {
      selector: ".ws-vineyard-tours-card",
      align: "end",
    });
  }, 0);
}

function seedVineyardToursCard() {
  const imageUrl = getTheme("vineyard_tours_image_url", null);
  if (!imageUrl) return;

  getMessages()
    .filter((m) => m.kind === "prompts")
    .forEach((m) => removeMessage(m.id));

  // Avoid duplicate cards on refresh / re-seed.
  getMessages()
    .filter((m) => m.kind === "vineyard-tours-card")
    .forEach((m) => removeMessage(m.id));

  addMessage({
    role: "bot",
    kind: "vineyard-tours-card",
    isFirstResponse: false,
    data: {
      image_url: imageUrl,
      caption:
        getTheme("vineyard_tours_caption", null) ||
        "Guests at a wine tasting at our Sebastopol Estate Vineyard Winery.",
      cta_label: "Learn More",
    },
  });

  scrollVineyardCardIntoView();
}

export async function register({ config, shadow }) {
  registerMessageKind("live-person-form", LivePersonForm);
  registerMessageKind("vineyard-tours-card", VineyardToursCard);

  registry.registerComponent("WineCard", HalleckWineCard);
  registry.registerComponent("EventCard", HalleckEventCard);
  registry.registerComponent("EventDetailsCard", HalleckEventDetailsCard);
  registry.registerComponent("MembershipCard", HalleckMembershipCard);

  // Old chatbot-banner: hero wraps <a href="…dry-white-zinfandel…">. Seed
  // banner_link_url when admin omits it (PromoPoster falls back to this key).
  const HALLECK_BANNER_PRODUCT =
    "https://shop.halleckvineyard.com/collections/all/products/dry-white-zinfandel-russian-river-not-your-mothers-2025";
  const t = getState().tenant || {};
  if (t.theme && !t.theme.banner_link_url) {
    setState({
      tenant: {
        ...t,
        theme: { ...t.theme, banner_link_url: HALLECK_BANNER_PRODUCT },
      },
    });
  }

  const cfg = getState().tenant || {};
  if (!Array.isArray(cfg.guided_flow_questions) || cfg.guided_flow_questions.length === 0) {
    setState({ tenant: { ...cfg, guided_flow_questions: getGuidedFlow() } });
  }

  const onVineyardTours = isVineyardToursPage();

  if (!onVineyardTours) {
    stripVineyardToursCards();
    // Vineyard visit strips chips from history — restore for normal pages.
    ensureGuidedPromptsInState();
  }

  // Old condition3: always auto-open on /vineyard-tours/ (every refresh too).
  maybeAutoOpenVineyardTours();
  // Homepage first-visit only (old _vi cookie).
  if (!onVineyardTours) maybeAutoOpenHome();

  on("ws-welcome-seeded", () => {
    if (!onVineyardTours) return;
    if (getMessages().some((m) => m.kind === "vineyard-tours-card")) {
      scrollVineyardCardIntoView();
      return;
    }
    seedVineyardToursCard();
  });

  if (onVineyardTours) {
    // Every open on this page — focus the vineyard banner (old scrollVineyardCardIntoView).
    on("ws-toggle", ({ isOpen } = {}) => {
      if (isOpen) scrollVineyardCardIntoView();
    });

    if (getMessages().some((m) => m.kind === "vineyard-tours-card")) {
      setTimeout(scrollVineyardCardIntoView, 100);
    } else if (getMessages().length > 0) {
      setTimeout(() => {
        if (getMessages().some((m) => m.kind === "vineyard-tours-card")) {
          scrollVineyardCardIntoView();
          return;
        }
        seedVineyardToursCard();
      }, 0);
    }
  }

  on("ws-prompt-select", ({ value }) => {
    if (value === "__text_live_person__") {
      showLivePersonForm({ fromVineyardTours: false });
      return;
    }
  });

  on("ws-vineyard-tours-learn-more", () => {
    showLivePersonForm({ fromVineyardTours: true });
  });

  on("ws-contact-submit", () => {
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: "We've received your information and we will text you between the hours of 9AM to 5PM PST." },
    });
    postDefaultOptions();
  });
}
